import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, Dumbbell } from 'lucide-react';
import { cardsApi } from '../../api/cards';
import { useLanguage } from '../../i18n/LanguageContext';
import { formatDateTime } from '../../utils/locale';

const MAX_SELECT = 20;

const CoachTrainingModal = ({ open, onClose, loanId, coachCardId, userId, coachName, onStarted }) => {
  const { t, locale } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [session, setSession] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [message, setMessage] = useState({ type: '', text: '' });

  const activeCardIds = useMemo(
    () => new Set((session?.activeAssignments || []).map((a) => Number(a.cardId))),
    [session?.activeAssignments]
  );

  const slotsRemaining = session?.slotsRemaining ?? MAX_SELECT;
  const periodStarted = !!session?.periodStarted;
  const maxSelectable = session?.maxSelectable ?? MAX_SELECT;

  const loadSession = useCallback(async () => {
    if (!userId || (!loanId && !coachCardId)) return;
    setLoading(true);
    setMessage({ type: '', text: '' });
    try {
      const res = await cardsApi.getCoachTrainingSession({ loanId, cardId: coachCardId, userId });
      if (res?.success && res.session) {
        setSession(res.session);
        setSelected(new Set());
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.message || t('coachTraining.loadError') });
    } finally {
      setLoading(false);
    }
  }, [loanId, coachCardId, userId, t]);

  useEffect(() => {
    if (open) loadSession();
  }, [open, loadSession]);

  const toggleCard = (cardId) => {
    if (activeCardIds.has(cardId)) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(cardId)) next.delete(cardId);
      else if (next.size < slotsRemaining) next.add(cardId);
      return next;
    });
  };

  const handleSubmit = async () => {
    if (selected.size === 0) {
      setMessage({ type: 'error', text: t('coachTraining.selectAtLeastOne') });
      return;
    }
    const trainees = (session?.eligibleCards || [])
      .filter((c) => selected.has(c.id))
      .map((c) => ({ cardId: c.id, templateId: c.template_id }));

    setSubmitting(true);
    setMessage({ type: '', text: '' });
    try {
      await cardsApi.startCoachTraining({
        loanId: loanId ?? session?.loanId ?? undefined,
        cardId: coachCardId,
        userId,
        trainees
      });
      setMessage({
        type: 'success',
        text: periodStarted ? t('coachTraining.added') : t('coachTraining.started')
      });
      onStarted?.();
      await loadSession();
    } catch (err) {
      setMessage({ type: 'error', text: err.message || t('coachTraining.startError') });
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const rarityLabel = session?.coachRarity
    ? session.coachRarity.charAt(0).toUpperCase() + session.coachRarity.slice(1)
    : '';

  const activeCount = session?.activeCount ?? activeCardIds.size;
  const hasEligibleToAdd = (session?.eligibleCards?.length ?? 0) > 0 && slotsRemaining > 0;
  const xpIntervalHours = session?.xpIntervalHours ?? session?.periodHours ?? 6;
  const xpPerInterval = session?.xpPerInterval ?? 1;
  const xpPerHour = Number(
    session?.xpPerHour ?? xpPerInterval / xpIntervalHours
  ).toFixed(2);

  return createPortal(
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/80" onClick={onClose}>
      <div
        className="coach-training-modal football-card glass-football max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <h2 className="text-xl font-bold text-[#1e293b] flex items-center gap-2">
              <Dumbbell className="w-6 h-6 text-blue-400 shrink-0" />
              {t('coachTraining.title')}
            </h2>
            <p className="text-sm text-gray-400 mt-1">
              {coachName || session?.coachName} · {rarityLabel}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-white transition-colors"
            aria-label={t('coachTraining.cancel')}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <p className="text-sm text-gray-300 mb-2">{t('coachTraining.hint', { rarity: rarityLabel, max: maxSelectable })}</p>
        <p className="text-xs text-gray-400 mb-1">
          {t('coachTraining.xpHint', {
            xp: xpPerInterval,
            xpPerHour,
            intervalHours: xpIntervalHours
          })}
        </p>
        {!periodStarted && !loading && (
          <p className="text-xs text-amber-300/90 mb-3 leading-snug">
            {t('coachTraining.periodStartsOnStart', { hours: session?.periodHours ?? 6 })}
          </p>
        )}

        {loading ? (
          <p className="text-sm text-gray-400 py-8 text-center">{t('coachTraining.loading')}</p>
        ) : (
          <>
            {periodStarted && (
              <div className="text-sm text-emerald-300 bg-emerald-950/50 border border-emerald-800/50 rounded px-3 py-2 mb-3 space-y-1">
                <p>
                  {t('coachTraining.active', {
                    hours: session?.periodHours ?? 6,
                    xp: xpPerInterval,
                    xpPerHour,
                    intervalHours: xpIntervalHours
                  })}
                </p>
                {session?.periodEndsAt && (
                  <p className="text-xs text-emerald-200/80">
                    {t('coachTraining.periodEndsAt', {
                      date: formatDateTime(new Date(session.periodEndsAt), {}, locale)
                    })}
                  </p>
                )}
              </div>
            )}
            <p className="text-xs text-gray-400 mb-2">
              {t('coachTraining.slotsRemaining', {
                count: activeCount,
                max: maxSelectable,
                remaining: slotsRemaining
              })}
            </p>
            <div className="flex-1 overflow-y-auto min-h-0 border border-gray-600/60 rounded-lg mb-3 bg-black/20">
              {(session?.activeAssignments?.length ?? 0) > 0 && (
                <>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide px-3 pt-2 pb-1">
                    {t('coachTraining.inTrainingSection')}
                  </p>
                  <ul className="divide-y divide-gray-700/80 border-b border-gray-700/80">
                    {(session?.activeAssignments || []).map((a) => {
                      const xp =
                        typeof a.xp_percentage === 'number'
                          ? a.xp_percentage
                          : parseFloat(a.xp_percentage) || 0;
                      return (
                        <li key={a.cardId} className="flex items-center gap-3 px-3 py-2 opacity-90">
                          <input type="checkbox" checked disabled className="w-4 h-4 accent-blue-500" />
                          <span className="flex-1 text-sm font-medium text-gray-200 truncate">{a.name}</span>
                          <span className="text-xs text-gray-400">{xp.toFixed(1)}% XP</span>
                        </li>
                      );
                    })}
                  </ul>
                </>
              )}
              {hasEligibleToAdd ? (
                <>
                  {periodStarted && (
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide px-3 pt-2 pb-1">
                      {t('coachTraining.addSection')}
                    </p>
                  )}
                  <ul className="divide-y divide-gray-700/80">
                    {(session?.eligibleCards || []).map((c) => {
                      const name = c.customName || c.playerName || `#${c.id}`;
                      const xp =
                        typeof c.xp_percentage === 'number'
                          ? c.xp_percentage
                          : parseFloat(c.xp_percentage) || 0;
                      const isSel = selected.has(c.id);
                      return (
                        <li key={c.id}>
                          <label className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-white/5 transition-colors">
                            <input
                              type="checkbox"
                              checked={isSel}
                              onChange={() => toggleCard(c.id)}
                              className="w-4 h-4 accent-blue-500"
                            />
                            <span className="flex-1 text-sm font-medium text-gray-200 truncate">{name}</span>
                            <span className="text-xs text-gray-400">{xp.toFixed(1)}% XP</span>
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                </>
              ) : (
                (session?.activeAssignments?.length ?? 0) === 0 && (
                  <p className="text-sm text-gray-400 p-4 text-center">{t('coachTraining.noEligible')}</p>
                )
              )}
            </div>
            {hasEligibleToAdd && (
              <p className="text-xs text-gray-400 mb-2">
                {t('coachTraining.selected', { count: selected.size, max: slotsRemaining })}
              </p>
            )}
          </>
        )}

        {message.text && (
          <p
            className={`text-sm mb-2 ${message.type === 'error' ? 'text-red-400' : 'text-emerald-400'}`}
          >
            {message.text}
          </p>
        )}

        <div className="flex gap-3 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-3 border-2 border-gray-500 text-gray-300 hover:bg-gray-700 font-semibold rounded-lg transition-all"
          >
            {t('coachTraining.cancel')}
          </button>
          {hasEligibleToAdd ? (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading || submitting || selected.size === 0}
              className="btn-football flex-1 disabled:opacity-50"
            >
              {submitting
                ? t('coachTraining.starting')
                : periodStarted
                  ? t('coachTraining.addCards')
                  : t('coachTraining.start')}
            </button>
          ) : (
            <button
              type="button"
              onClick={onClose}
              className="btn-football flex-1"
            >
              {t('coachTraining.close')}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CoachTrainingModal;
